import { createContext, useCallback, useEffect, useImperativeHandle, useState } from 'react'

import {
  DateTimePickerContextType,
  DateTimePickerHandle,
  DateTimePickerProps,
  DateTimePickerState,
  UseDateTimePickerHook,
  VisibleSectionEnum,
} from './interfaces'

export const DateTimePickerContext = createContext<DateTimePickerContextType>({
  dateTimeState: {
    value: {},
    datePicker: {
      month: new Date().getMonth(),
      year: new Date().getFullYear(),
    },
    visibleSection: VisibleSectionEnum.DATE,
    isExpanded: true,
    isLoading: false,
    isProlongation: false,
    props: {},
  },
  setDateTimeState: () => {
    console.log('Not loaded yet!')
  },
})

const getInitialDateTimePickerState = (props: DateTimePickerProps): DateTimePickerState => ({
  value: {
    day: props.defaultDate?.getDate(),
    month: props.defaultDate?.getMonth(),
    year: props.defaultDate?.getFullYear(),
    time: props.defaultTime ?? undefined,
  },
  datePicker: {
    month: props.defaultDate?.getMonth() ?? new Date().getMonth(),
    year: props.defaultDate?.getFullYear() ?? new Date().getFullYear(),
  },
  visibleSection:
    props.defaultVisibleSection ??
    (Boolean(props.defaultDate && props.defaultTime) ? VisibleSectionEnum.TIME : VisibleSectionEnum.DATE),
  isLoading: Boolean(props.defaultIsLoading),
  isExpanded: !props.defaultCollapsed,
  isProlongation: props.isProlongation,
  props,
})

export const useDateTimePickerLogic: UseDateTimePickerHook = (props, ref) => {
  const [dateTimeState, setDateTimeState] = useState<DateTimePickerState>(getInitialDateTimePickerState(props))

  useEffect(() => {
    setDateTimeState((state) => ({ ...state, props }))
  }, [
    props.title,
    props.isPickup,
    props.isProlongation,
    props.defaultIsLoading,
    props.defaultCollapsed,
    props.defaultDate,
    props.defaultTime,
    props.defaultVisibleSection,
    props.disabled,
    props.shouldDisableDate,
    props.shouldDisableHour,
    props.shouldDisableDateButton,
    props.shouldDisableTimeButton,
    props.onChange,
    props.onDisplayedMonthChange,
  ])

  const collapse = useCallback(() => {
    setDateTimeState((state) => ({ ...state, isExpanded: false }))
  }, [])

  const expand = useCallback(() => {
    setDateTimeState((state) => ({ ...state, isExpanded: true }))
  }, [])

  const reset = useCallback(() => {
    setDateTimeState((state) => ({ ...state, value: {} }))
  }, [])

  const displayLoader = useCallback(() => {
    setDateTimeState((state) => ({ ...state, isLoading: true }))
  }, [])

  const hideLoader = useCallback(() => {
    setDateTimeState((state) => ({ ...state, isLoading: false }))
  }, [])

  const changeDisplayedMonth = useCallback((newMonth: number, newYear: number) => {
    setDateTimeState((state) => ({
      ...state,
      datePicker: {
        ...state.datePicker,
        month: newMonth,
        year: newYear,
      },
    }))
  }, [])

  const changeVisibleSection = useCallback((newSection: VisibleSectionEnum) => {
    setDateTimeState((state) => ({
      ...state,
      visibleSection: newSection,
    }))
  }, [])

  const utils: DateTimePickerHandle = {
    collapse,
    expand,
    reset,
    displayLoader,
    hideLoader,
    changeDisplayedMonth,
    changeVisibleSection,
  }

  useImperativeHandle(ref, () => utils)

  return [dateTimeState, setDateTimeState]
}
